import React, { useRef, useEffect } from "react";

/**
 * AsistanOrb — Siri benzeri dijital asistan topu (canvas).
 *  - speaking: konuşurken daha canlı/titreşimli.
 *  - analyser: (opsiyonel) Web Audio AnalyserNode -> sese göre gerçek titreşim.
 * KRİTİK: analyser deps'te DEĞİL (analyserRef ile okunur) -> her yeni TTS mesajında
 * analyser değişse bile canvas re-init/flicker olmaz.
 */
const AsistanOrb = ({ size = 120, speaking = false, analyser = null }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const speakingRef = useRef(speaking);
  const analyserRef = useRef(analyser);
  const dataRef = useRef(null);
  speakingRef.current = speaking;
  analyserRef.current = analyser;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    const cx = size / 2;
    const cy = size / 2;
    const baseR = size * 0.3;
    let t = Math.random() * 10;
    let ampS = 0.05;      // yumuşatılmış genel amplitüd
    let bassS = 0;        // yumuşatılmış bas
    const ripples = [];   // onset halkaları
    let lastOnset = 0;
    let frame = 0;

    const blobs = [
      { color: "rgba(33,150,243,0.55)", rr: 1.0, n: 3, sp: 1.0 },
      { color: "rgba(3,169,244,0.5)", rr: 0.92, n: 4, sp: -1.35 },
      { color: "rgba(0,229,255,0.42)", rr: 0.82, n: 5, sp: 1.75 },
    ];

    const draw = () => {
      frame++;
      ctx.clearRect(0, 0, size, size);
      const an = analyserRef.current;
      let amp = speakingRef.current ? 0.14 : 0.05;
      let bass = 0;
      if (an && speakingRef.current) {
        try {
          const bins = an.frequencyBinCount;
          if (!dataRef.current || dataRef.current.length !== bins) dataRef.current = new Uint8Array(bins);
          an.getByteFrequencyData(dataRef.current);
          const d = dataRef.current;
          let sum = 0;
          for (let i = 0; i < d.length; i++) sum += d[i];
          amp = 0.05 + (sum / d.length / 255) * 0.42;
          let bsum = 0;
          const bn = Math.max(1, Math.min(8, d.length));
          for (let i = 0; i < bn; i++) bsum += d[i];
          bass = bsum / bn / 255;
        } catch (e) { /* kapalı ctx -> idle */ }
      }
      // yumuşat (lerp)
      ampS += (amp - ampS) * 0.35;
      bassS += (bass - bassS) * 0.3;

      // onset (ani ses artışı) -> genişleyen halka
      if (!reduce && speakingRef.current && ampS > 0.42 && frame - lastOnset > 11) {
        lastOnset = frame;
        ripples.push({ r: baseR * 0.7, a: 0.5 });
      }

      const pulse = reduce ? 1 : 1 + bassS * 0.08;

      // dış parıltı
      const glow = ctx.createRadialGradient(cx, cy, baseR * 0.2, cx, cy, baseR * 1.8);
      glow.addColorStop(0, `rgba(33,150,243,${0.28 + ampS * 0.3})`);
      glow.addColorStop(1, "rgba(33,150,243,0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, baseR * 1.8, 0, Math.PI * 2);
      ctx.fill();

      // onset halkaları
      for (let i = ripples.length - 1; i >= 0; i--) {
        const rp = ripples[i];
        rp.r += 1.6;
        rp.a -= 0.02;
        if (rp.a <= 0) { ripples.splice(i, 1); continue; }
        ctx.strokeStyle = `rgba(0,229,255,${rp.a})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, rp.r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // dalgalı bloblar
      blobs.forEach((b, bi) => {
        ctx.beginPath();
        const steps = 64;
        for (let i = 0; i <= steps; i++) {
          const a = (i / steps) * Math.PI * 2;
          const wob = 1 + ampS * Math.sin(a * b.n + t * b.sp + bi * 1.6);
          const r = baseR * b.rr * wob * pulse;
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fillStyle = b.color;
        ctx.fill();
      });

      // parlak çekirdek (amp ile parlar)
      const core = ctx.createRadialGradient(cx - baseR * 0.22, cy - baseR * 0.22, 0, cx, cy, baseR * 0.85 * pulse);
      core.addColorStop(0, `rgba(255,255,255,${0.82 + ampS * 0.18})`);
      core.addColorStop(0.45, "rgba(130,205,255,0.55)");
      core.addColorStop(1, "rgba(33,150,243,0.08)");
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(cx, cy, baseR * 0.68 * pulse, 0, Math.PI * 2);
      ctx.fill();

      // dönen speküler parıltı
      if (!reduce) {
        const sa = t * 0.6;
        const sx = cx + Math.cos(sa) * baseR * 0.32;
        const sy = cy + Math.sin(sa) * baseR * 0.32;
        const spec = ctx.createRadialGradient(sx, sy, 0, sx, sy, baseR * 0.4);
        spec.addColorStop(0, `rgba(255,255,255,${0.35 + ampS * 0.25})`);
        spec.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = spec;
        ctx.beginPath();
        ctx.arc(sx, sy, baseR * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }

      t += speakingRef.current ? 0.05 + ampS * 0.05 : 0.018;
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [size]);

  return <canvas ref={canvasRef} style={{ width: size, height: size, display: "block" }} aria-hidden="true" />;
};

export default AsistanOrb;
